import React, { useState, useEffect, useCallback } from 'react';
import { FinancialService } from '@/services/financial.service';
import { FinancialSummary, RevenueBreakdown, TransactionSummary } from '@/models/financial-report.model';

export const FinancialDashboard: React.FC = () => {
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [revenueBreakdown, setRevenueBreakdown] = useState<RevenueBreakdown | null>(null);
  const [transactionSummary, setTransactionSummary] = useState<TransactionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'month' | 'quarter' | 'year'>('month');

  const loadFinancialData = useCallback(async () => {
    setLoading(true);
    const now = new Date();
    let startDate: Date;
    const endDate = now;

    switch (period) {
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'quarter': {
        const quarter = Math.floor(now.getMonth() / 3);
        startDate = new Date(now.getFullYear(), quarter * 3, 1);
        break;
      }
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
    }

    try {
      const [summaryData, revenueData, transactionData] = await Promise.all([
        FinancialService.getFinancialSummary(startDate, endDate),
        FinancialService.getRevenueBreakdown(startDate, endDate),
        FinancialService.getTransactionSummary(startDate, endDate),
      ]);

      setSummary(summaryData);
      setRevenueBreakdown(revenueData);
      setTransactionSummary(transactionData);
    } catch (error: any) {
      console.error('Error loading financial data:', error);
      console.error('Error details:', {
        code: error?.code,
        message: error?.message,
        stack: error?.stack,
      });
      // Set empty data on error to prevent UI crashes
      setSummary({
        totalRevenue: 0,
        subscriptionRevenue: 0,
        transactionFees: 0,
        otherRevenue: 0,
        totalExpenses: 0,
        payrollExpenses: 0,
        infrastructureExpenses: 0,
        marketingExpenses: 0,
        otherExpenses: 0,
        netIncome: 0,
        grossMargin: 0,
        periodStart: startDate,
        periodEnd: endDate,
        periodType: period === 'month' ? 'monthly' : period === 'quarter' ? 'quarterly' : 'yearly',
      });
      setRevenueBreakdown({
        bySource: {
          employerSubscriptions: 0,
          providerSubscriptions: 0,
          transactionFees: 0,
          platformFees: 0,
          other: 0,
        },
        byEmployer: [],
        byProvider: [],
        trends: [],
      });
      setTransactionSummary({
        totalTransactions: 0,
        totalAmount: 0,
        successfulTransactions: 0,
        failedTransactions: 0,
        pendingTransactions: 0,
        averageTransactionAmount: 0,
        byType: {
          employerPayments: 0,
          memberReimbursements: 0,
          providerPayouts: 0,
          subscriptionPayments: 0,
        },
      });
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadFinancialData();
  }, [loadFinancialData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading financial data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Financial Dashboard</h1>
            <p className="mt-1 text-sm text-gray-500">Track revenue, expenses, and financial performance</p>
          </div>
          <div className="flex space-x-2">
            {(['month', 'quarter', 'year'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  period === p
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Financial Summary Cards */}
        {summary && (
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Financial Summary</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Total Revenue"
                value={formatCurrency(summary.totalRevenue)}
                icon="💰"
                color="green"
              />
              <StatCard
                label="Total Expenses"
                value={formatCurrency(summary.totalExpenses)}
                icon="💸"
                color="red"
              />
              <StatCard
                label="Net Income"
                value={formatCurrency(summary.netIncome)}
                icon="📊"
                color={summary.netIncome >= 0 ? 'green' : 'red'}
              />
              <StatCard
                label="Gross Margin"
                value={formatPercent(summary.grossMargin)}
                icon="📈"
                color={summary.grossMargin >= 0 ? 'green' : 'red'}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Breakdown */}
          {revenueBreakdown && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Breakdown</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Employer Subscriptions</span>
                  <span className="font-semibold">{formatCurrency(revenueBreakdown.bySource.employerSubscriptions)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Provider Subscriptions</span>
                  <span className="font-semibold">{formatCurrency(revenueBreakdown.bySource.providerSubscriptions)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Transaction Fees</span>
                  <span className="font-semibold">{formatCurrency(revenueBreakdown.bySource.transactionFees)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Platform Fees</span>
                  <span className="font-semibold">{formatCurrency(revenueBreakdown.bySource.platformFees)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Other</span>
                  <span className="font-semibold">{formatCurrency(revenueBreakdown.bySource.other)}</span>
                </div>
                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">Total Revenue</span>
                    <span className="font-bold text-lg">{formatCurrency(summary?.totalRevenue || 0)}</span>
                  </div>
                </div>
              </div>

              {revenueBreakdown.byEmployer.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Top Employers</h4>
                  <div className="space-y-2">
                    {revenueBreakdown.byEmployer.slice(0, 5).map((employer, idx) => (
                      <div key={employer.employerId} className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">{idx + 1}. {employer.employerName}</span>
                        <span className="font-medium">{formatCurrency(employer.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Expense Breakdown */}
          {summary && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Expense Breakdown</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Payroll</span>
                  <span className="font-semibold">{formatCurrency(summary.payrollExpenses)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Infrastructure</span>
                  <span className="font-semibold">{formatCurrency(summary.infrastructureExpenses)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Marketing</span>
                  <span className="font-semibold">{formatCurrency(summary.marketingExpenses)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Other</span>
                  <span className="font-semibold">{formatCurrency(summary.otherExpenses)}</span>
                </div>
                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">Total Expenses</span>
                    <span className="font-bold text-lg">{formatCurrency(summary.totalExpenses)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Transaction Summary */}
        {transactionSummary && (
          <div className="mt-6 bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Transaction Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Total Transactions</p>
                <p className="text-2xl font-bold text-gray-900">{transactionSummary.totalTransactions}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Successful</p>
                <p className="text-2xl font-bold text-green-600">{transactionSummary.successfulTransactions}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Failed</p>
                <p className="text-2xl font-bold text-red-600">{transactionSummary.failedTransactions}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Average Amount</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(transactionSummary.averageTransactionAmount)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: string;
  icon: string;
  color: 'green' | 'red' | 'blue';
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color, subtitle }) => {
  const colorClasses = {
    green: 'bg-green-50 text-green-600 border-green-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
  };

  return (
    <div className={`rounded-lg border-2 p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{label}</p>
          <p className="text-2xl font-bold mt-2">{value}</p>
          {subtitle && (
            <p className="text-xs opacity-75 mt-1">{subtitle}</p>
          )}
        </div>
        <div className="text-3xl opacity-75">{icon}</div>
      </div>
    </div>
  );
};

